/**
 * Página de agendamentos — visual design system.
 *
 * Compõe PageHeader + EmptyState + Alert + Spinner + Button + Badge do DS.
 * Toast via sonner (molecule Toast) substitui window.alert/confirm.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { Badge } from '@/design-system/atoms/Badge'
import { Button } from '@/design-system/atoms/Button'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Alert } from '@/design-system/molecules/Alert'
import { Card } from '@/design-system/molecules/Card'
import { EmptyState } from '@/design-system/molecules/EmptyState'
import { toast } from '@/design-system/molecules/Toast'
import { PageHeader } from '@/design-system/organisms/PageHeader'

import ExecutionHistoryPanel from '../../components/scheduler/ExecutionHistoryPanel'
import ScheduledTaskCard from '../../components/scheduler/ScheduledTaskCard'
import ScheduledTaskModal from '../../components/scheduler/ScheduledTaskModal'
import { useAuth } from '../../context/auth'
import { PageTitleService } from '../../hooks/usePageTitle'
import schedulerService from '../../services/schedulerService'
import type {
  SchedulableTool,
  ScheduledTask,
  ScheduledTaskCreatePayload,
} from '../../types/scheduler'

export default function SchedulerDashboard() {
  const navigate = useNavigate()
  const { features, permissions } = useAuth()

  const [tasks, setTasks] = useState<ScheduledTask[]>([])
  const [tools, setTools] = useState<SchedulableTool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ScheduledTask | null>(null)
  const [historyTaskId, setHistoryTaskId] = useState<string | null>(null)

  useEffect(() => {
    PageTitleService.setTitle('Agendamentos')
  }, [])

  const canAccess = useMemo(
    () =>
      Boolean(
        features?.scheduled_tasks_enabled && permissions?.canManageSchedules
      ),
    [features?.scheduled_tasks_enabled, permissions?.canManageSchedules]
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [taskList, toolList] = await Promise.all([
        schedulerService.listTasks(),
        schedulerService.listTools(),
      ])
      setTasks(taskList)
      setTools(toolList)
    } catch (err) {
      console.error('[Scheduler] Failed to load data', err)
      setError(
        'Não foi possível carregar os agendamentos. Tente novamente em instantes.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canAccess) void loadData()
  }, [canAccess, loadData])

  const handleCreate = async (payload: ScheduledTaskCreatePayload) => {
    if (editing) {
      await schedulerService.updateTask(editing.id, payload)
      toast.success('Agendamento atualizado')
    } else {
      await schedulerService.createTask(payload)
      toast.success('Agendamento criado')
    }
    setModalOpen(false)
    setEditing(null)
    await loadData()
  }

  const handleToggleActive = async (task: ScheduledTask) => {
    setTasks(prev =>
      prev.map(t => (t.id === task.id ? { ...t, is_active: !t.is_active } : t))
    )
    try {
      await schedulerService.updateTask(task.id, { is_active: !task.is_active })
      toast.success(
        task.is_active ? 'Agendamento pausado' : 'Agendamento ativado'
      )
    } catch {
      toast.error('Não foi possível alterar o status')
      await loadData()
    }
  }

  const handleDelete = async (task: ScheduledTask) => {
    if (!window.confirm(`Excluir o agendamento "${task.name}"?`)) return
    try {
      await schedulerService.deleteTask(task.id)
      toast.success('Agendamento excluído')
      await loadData()
    } catch {
      toast.error('Falha ao excluir agendamento')
    }
  }

  const handleRunNow = async (task: ScheduledTask) => {
    try {
      await schedulerService.runNow(task.id)
      toast.success('Execução enfileirada — acompanhe no histórico', {
        description: task.name,
      })
    } catch {
      toast.error('Falha ao disparar execução')
    }
  }

  if (!canAccess) {
    return <AccessDenied onBack={() => navigate('/')} />
  }

  const activeCount = tasks.filter(t => t.is_active).length
  const newButton = (
    <Button
      variant="primary"
      leftIcon={<Icon name="plus" size={16} />}
      onClick={() => {
        setEditing(null)
        setModalOpen(true)
      }}
    >
      Novo agendamento
    </Button>
  )

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader
        eyebrow="Automação"
        title="Agendamentos"
        description="Execute tools do agente de garantias em horários recorrentes ou em data específica."
        actions={
          <div className="flex items-center gap-3">
            {tasks.length > 0 && (
              <Badge variant="primary" shape="pill" size="md">
                {activeCount} de {tasks.length} ativo
                {tasks.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {newButton}
          </div>
        }
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" className="text-primary-600" />
        </div>
      )}

      {error && !loading && (
        <Alert variant="error" title="Falha ao carregar dados">
          {error}
        </Alert>
      )}

      {!loading && !error && tasks.length === 0 && (
        <Card padding="lg" variant="outlined" radius="2xl">
          <EmptyState
            iconName="calendar-days"
            title="Nenhum agendamento configurado"
            description="Automatize relatórios recorrentes do agente de garantias e receba os resultados por e-mail ou WhatsApp."
            action={newButton}
          />
        </Card>
      )}

      {!loading && !error && tasks.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tasks.map(task => (
            <ScheduledTaskCard
              key={task.id}
              task={task}
              onToggle={() => void handleToggleActive(task)}
              onEdit={() => {
                setEditing(task)
                setModalOpen(true)
              }}
              onDelete={() => void handleDelete(task)}
              onRunNow={() => void handleRunNow(task)}
              onViewHistory={() => setHistoryTaskId(task.id)}
            />
          ))}
        </div>
      )}

      {modalOpen && (
        <ScheduledTaskModal
          tools={tools}
          whatsappEnabled={Boolean(features?.whatsapp_enabled)}
          editing={editing}
          onClose={() => {
            setModalOpen(false)
            setEditing(null)
          }}
          onSubmit={handleCreate}
        />
      )}

      {historyTaskId && (
        <ExecutionHistoryPanel
          taskId={historyTaskId}
          taskName={tasks.find(t => t.id === historyTaskId)?.name}
          onClose={() => setHistoryTaskId(null)}
        />
      )}
    </div>
  )
}

function AccessDenied({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <Card padding="lg" radius="2xl" className="max-w-md text-center">
        <EmptyState
          iconName="lock"
          title="Funcionalidade indisponível"
          description="O módulo de agendamentos não está habilitado ou você não tem permissão para acessá-lo."
          action={
            <Button variant="primary" onClick={onBack}>
              Voltar
            </Button>
          }
        />
      </Card>
    </div>
  )
}
